import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';

/**
 * Launching Google's Drive Picker for a pending access request.
 *
 * With the drive.file OAuth scope the backend can only see files it created or
 * files the user explicitly granted, and the Picker is the only way to add a
 * grant. Shared by the app-wide listener and the /grant-access deep link page.
 */

export interface DriveAccessRequestData {
  /** Persisted request id — present for stored requests, absent for legacy events */
  requestId?: string | null;
  threadId: string;
  agentId: string;
  oauthId: number;
  fileType: 'document' | 'spreadsheet' | 'presentation' | 'any';
  fileName?: string | null;
}

export interface PickerDoc {
  id: string;
  name?: string;
  mimeType?: string;
  url?: string;
}

interface PickerResult {
  action: string;
  docs?: PickerDoc[];
}

interface PickerView {
  setIncludeFolders(include: boolean): PickerView;
  setSelectFolderEnabled(enabled: boolean): PickerView;
  setMimeTypes(mimeTypes: string): PickerView;
}

interface PickerInstance {
  setVisible(visible: boolean): void;
}

interface PickerBuilder {
  setOAuthToken(token: string): PickerBuilder;
  addView(view: PickerView): PickerBuilder;
  setCallback(callback: (result: PickerResult) => void): PickerBuilder;
  enableFeature(feature: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(appId: string): PickerBuilder;
  build(): PickerInstance;
}

interface GooglePickerNamespace {
  picker: {
    DocsView: new (viewId: string) => PickerView;
    PickerBuilder: new () => PickerBuilder;
    ViewId: { DOCS: string };
    Action: { PICKED: string; CANCEL: string };
    Feature: { MULTISELECT_ENABLED: string };
  };
}

interface GapiNamespace {
  load(
    library: string,
    options: { callback: () => void; onerror: () => void },
  ): void;
}

const getGapi = (): GapiNamespace | undefined =>
  (window as unknown as { gapi?: GapiNamespace }).gapi;

const getGooglePicker = (): GooglePickerNamespace | undefined =>
  (window as unknown as { google?: GooglePickerNamespace }).google;

const GOOGLE_MIME_TYPES: Record<string, string> = {
  document: 'application/vnd.google-apps.document',
  spreadsheet: 'application/vnd.google-apps.spreadsheet',
  presentation: 'application/vnd.google-apps.presentation',
};

export const FILE_TYPE_LABELS: Record<string, string> = {
  document: 'document',
  spreadsheet: 'spreadsheet',
  presentation: 'presentation',
  any: 'file',
};

/** Human label for the file a request is about, e.g. `your spreadsheet "Q3"`. */
export function describeRequestedFile(request: DriveAccessRequestData): string {
  const typeLabel = FILE_TYPE_LABELS[request.fileType] || FILE_TYPE_LABELS.any;
  return request.fileName
    ? `your ${typeLabel} "${request.fileName}"`
    : `a ${typeLabel} in your Google Drive`;
}

/** Load the Google API + Picker scripts once and cache the promise. */
let pickerLoadPromise: Promise<void> | null = null;
export function loadPickerApi(): Promise<void> {
  if (pickerLoadPromise) return pickerLoadPromise;
  pickerLoadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      'script[src*="apis.google.com/js/api.js"]',
    );
    const onApiReady = () => {
      const gapi = getGapi();
      if (!gapi) {
        reject(new Error('Google API script loaded but gapi is missing'));
        return;
      }
      gapi.load('picker', {
        callback: () => resolve(),
        onerror: () => reject(new Error('Failed to load Google Picker')),
      });
    };
    if (existing && getGapi()) {
      onApiReady();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.onload = onApiReady;
    script.onerror = () =>
      reject(new Error('Failed to load Google API script'));
    document.head.appendChild(script);
  });
  return pickerLoadPromise;
}

export const PICKER_OPEN_ERROR =
  'The Google file picker could not be opened. You can paste the file link in the chat instead.';

interface OpenGoogleFilePickerOptions {
  /** The linked Google account whose Drive is browsed (and granted from). */
  oauthId: number;
  /** Dream API bearer token, used to fetch the short-lived Picker config. */
  authToken: string;
  /** Restrict the view to these Drive MIME types; omit for any file. */
  mimeTypes?: string[];
  onPicked: (files: PickerDoc[]) => void;
  onCancelled?: () => void;
}

/**
 * Loads the Picker, fetches its config for the account and shows it. Resolves
 * with the instance once it is on screen; rejects if it could not be opened.
 * The Picker renders in its own fixed layer above any modal.
 */
export async function openGoogleFilePicker(
  options: OpenGoogleFilePickerOptions,
): Promise<PickerInstance> {
  const [{ data: config }] = await Promise.all([
    api.get<{
      accessToken: string;
      apiKey: string | null;
      appId: string | null;
    }>(`/google-link/picker-config?oauth_id=${options.oauthId}`, {
      headers: { Authorization: `Bearer ${options.authToken}` },
    }),
    loadPickerApi(),
  ]);

  const google = getGooglePicker();
  if (!google) {
    throw new Error('Google Picker API not available');
  }
  const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(false);
  if (options.mimeTypes && options.mimeTypes.length > 0) {
    view.setMimeTypes(options.mimeTypes.join(','));
  }

  let builder = new google.picker.PickerBuilder()
    .setOAuthToken(config.accessToken)
    .addView(view)
    .setCallback((result: PickerResult) => {
      if (result.action === google.picker.Action.PICKED) {
        options.onPicked(
          (result.docs ?? []).map((doc) => ({
            id: doc.id,
            name: doc.name,
            mimeType: doc.mimeType,
            url: doc.url,
          })),
        );
      } else if (result.action === google.picker.Action.CANCEL) {
        options.onCancelled?.();
      }
    })
    .enableFeature(google.picker.Feature.MULTISELECT_ENABLED);
  if (config.apiKey) builder = builder.setDeveloperKey(config.apiKey);
  if (config.appId) builder = builder.setAppId(config.appId);

  const picker = builder.build();
  picker.setVisible(true);
  return picker;
}

/**
 * Requests this browser session has already answered. The prompt is shown both
 * by the deep link page and by the app-wide listener, which re-mounts on
 * navigation and fetches outstanding requests — that fetch can overlap the POST
 * resolving a request, so answered ids are remembered rather than trusting the
 * round trip to have landed.
 */
const answeredRequestIds = new Set<string>();

export const isRequestAnswered = (requestId?: string | null): boolean =>
  !!requestId && answeredRequestIds.has(requestId);

interface UseGoogleDrivePickerResult {
  opening: boolean;
  /**
   * Google's Picker is on screen. Callers must not render a modal of their own
   * while it is: the Picker sits in its own fixed layer, so a second overlay
   * covers it and swallows taps.
   */
  pickerVisible: boolean;
  pickerError: string | null;
  setPickerError: (error: string | null) => void;
  /** Opens Google's Picker; resolves once it is visible (or has failed). */
  openPicker: (request: DriveAccessRequestData) => Promise<void>;
  /** Tells the agent the user declined, so it stops waiting. */
  cancelRequest: (request: DriveAccessRequestData) => Promise<void>;
}

export function useGoogleDrivePicker(opts?: {
  onPicked?: () => void;
  onCancelled?: () => void;
}): UseGoogleDrivePickerResult {
  const { getAccessTokenSilently } = useAuth0();
  const activePickerRef = useRef<PickerInstance | null>(null);
  const [opening, setOpening] = useState(false);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const callbacksRef = useRef(opts);
  callbacksRef.current = opts;

  useEffect(
    () => () => {
      if (activePickerRef.current) {
        activePickerRef.current.setVisible(false);
        activePickerRef.current = null;
      }
      setPickerVisible(false);
    },
    [],
  );

  const postSelection = useCallback(
    async (
      request: DriveAccessRequestData,
      payload: { files?: PickerDoc[]; cancelled?: boolean },
    ) => {
      if (request.requestId) answeredRequestIds.add(request.requestId);
      try {
        const authToken = await getAccessTokenSilently();
        await api.post(
          '/google-link/picker-selection',
          {
            threadId: request.threadId,
            agentId: request.agentId,
            oauthId: request.oauthId,
            requestId: request.requestId ?? undefined,
            ...payload,
          },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
      } catch (err) {
        console.error('[DrivePicker] Failed to post selection:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const openPicker = useCallback(
    async (request: DriveAccessRequestData) => {
      setOpening(true);
      setPickerError(null);
      try {
        const token = await getAccessTokenSilently();
        const mimeType = GOOGLE_MIME_TYPES[request.fileType];
        activePickerRef.current = await openGoogleFilePicker({
          oauthId: request.oauthId,
          authToken: token,
          mimeTypes: mimeType ? [mimeType] : undefined,
          onPicked: (files) => {
            setPickerVisible(false);
            void postSelection(request, { files });
            callbacksRef.current?.onPicked?.();
          },
          onCancelled: () => {
            setPickerVisible(false);
            void postSelection(request, { cancelled: true });
            callbacksRef.current?.onCancelled?.();
          },
        });
        setPickerVisible(true);
      } catch (err) {
        console.error('[DrivePicker] Failed to open picker:', err);
        setPickerVisible(false);
        setPickerError(PICKER_OPEN_ERROR);
      } finally {
        setOpening(false);
      }
    },
    [getAccessTokenSilently, postSelection],
  );

  const cancelRequest = useCallback(
    (request: DriveAccessRequestData) =>
      postSelection(request, { cancelled: true }),
    [postSelection],
  );

  return {
    opening,
    pickerVisible,
    pickerError,
    setPickerError,
    openPicker,
    cancelRequest,
  };
}
